/**
 * Slide-master tools — REAL master-slide branding and theme colors.
 *
 * Second-pass finding (2026-07-02, from the installed @types/office-js):
 * `SlideMaster.shapes` is the same mutable ShapeCollection as slides
 * (PowerPointApi 1.3 typing), and `ThemeColorScheme.setThemeColor` is
 * writable at PowerPointApi 1.10 — overturning the earlier "masters are
 * read-only" assumption. Whether a given host build ACCEPTS shape adds on
 * the master is confirmed live: these tools surface the exact Office error
 * if it rejects, which is itself the answer.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts.
 */

import type { PptxTool } from "../tool-types";
import {
  buildShape,
  ensureImageFills,
  shapePlanFromArgs,
  resolveLayout,
  GEOMETRIC_SHAPE_TYPES,
  type ShapePlan,
  type ShapeSurface,
} from "../shape-builders";
import { resolveSlideImage, computeLogoRect } from "./layout";
import { recordImageProvenance } from "../image-provenance";
import { CANVAS } from "../slide-templates";

/** Theme color slot names accepted by set_theme_colors (→ enum literals). */
const THEME_SLOTS: Record<string, string> = {
  dark1: "Dark1",
  dark2: "Dark2",
  light1: "Light1",
  light2: "Light2",
  accent1: "Accent1",
  accent2: "Accent2",
  accent3: "Accent3",
  accent4: "Accent4",
  accent5: "Accent5",
  accent6: "Accent6",
  hyperlink: "Hyperlink",
  followedHyperlink: "FollowedHyperlink",
};

// ─── Shared master/layout structural helpers ────────────────────────────────
//
// The master and its layouts are both ShapeSurfaces (their `.shapes` is the same
// mutable ShapeCollection as a slide's) and both expose `.background.fill`. So
// the background-fill and shape-batch logic is written once here and applied to
// whichever surface the caller resolved (master or a named layout).

/** Load the first slide master; optionally load its layout names too. */
async function firstMaster(
  context: PowerPoint.RequestContext,
  opts?: { layouts?: boolean }
): Promise<PowerPoint.SlideMaster> {
  const masters = context.presentation.slideMasters;
  masters.load("items");
  await context.sync();
  if (masters.items.length === 0) throw new Error("No slide masters in this presentation.");
  const master = masters.items[0];
  if (opts?.layouts) {
    master.layouts.load("items/name");
    await context.sync();
  }
  return master;
}

/** A resolved background instruction: a picture fill OR a solid colour. */
interface BackgroundSpec {
  color?: string;
  transparency?: number;
  image?: { base64: string };
}

/**
 * Turn background args into a BackgroundSpec — an image source (reusing the
 * same resolveSlideImage contract as apply_master_branding) wins over a solid
 * colour. Network/file work happens here, BEFORE PowerPoint.run.
 */
async function resolveBackgroundSpec(args: Record<string, unknown>): Promise<BackgroundSpec> {
  const image = await resolveSlideImage({
    useUploadedImage: args.useUploadedImage,
    useGeneratedImage: args.useGeneratedImage,
    imageBase64: args.imageBase64,
    imageUrl: args.imageUrl,
  });
  const transparency = typeof args.transparency === "number" ? args.transparency : undefined;
  if (image) return { image: { base64: image.base64 }, transparency };
  if (typeof args.color === "string") return { color: args.color, transparency };
  throw new Error(
    "Provide a background color (hex) or an image source (useUploadedImage/useGeneratedImage/imageBase64/imageUrl)."
  );
}

/** Apply a BackgroundSpec to a surface's background fill and sync. */
async function applyBackgroundFill(
  context: PowerPoint.RequestContext,
  fill: PowerPoint.SlideBackgroundFill,
  spec: BackgroundSpec
): Promise<void> {
  if (spec.image) {
    // Picture-fill options require an explicit transparency (no default).
    fill.setPictureOrTextureFill({ imageBase64: spec.image.base64, transparency: spec.transparency ?? 0 });
  } else if (spec.color) {
    fill.setSolidFill({ color: spec.color, transparency: spec.transparency });
  }
  await context.sync();
}

/** Parse the `shapes` array into validated plans (throws before any Office work). */
function parseShapeEntries(
  args: Record<string, unknown>
): Array<{ plan: ShapePlan; sendToBack: boolean }> {
  const specs = Array.isArray(args.shapes) ? (args.shapes as Record<string, unknown>[]) : [];
  if (specs.length === 0) throw new Error("Provide a non-empty `shapes` array.");
  return specs.map((s) => ({ plan: shapePlanFromArgs(s), sendToBack: s.sendToBack === true }));
}

/**
 * Build each plan onto the surface, then push any `sendToBack` shapes behind the
 * surface's placeholders (background bands/blocks). setZOrder is PowerPointApi
 * 1.8. Returns the created shape ids.
 */
async function addPlansToSurface(
  context: PowerPoint.RequestContext,
  surface: ShapeSurface,
  entries: Array<{ plan: ShapePlan; sendToBack: boolean }>
): Promise<Array<{ id: string }>> {
  const built = entries.map((e) => ({ shape: buildShape(surface, e.plan), sendToBack: e.sendToBack }));
  built.forEach((b) => b.shape.load("id"));
  await context.sync();
  const backs = built.filter((b) => b.sendToBack);
  if (backs.length > 0) {
    backs.forEach((b) =>
      b.shape.setZOrder("SendToBack" as unknown as PowerPoint.ShapeZOrder)
    );
    await context.sync();
  }
  return built.map((b) => ({ id: b.shape.id }));
}

/** Metadata returned for each shape a removal touched. */
interface RemovedShape {
  id: string;
  name: string;
  type: string;
}

interface RemoveOptions {
  shapeIds: string[];
  deleteAll: boolean;
  includePlaceholders: boolean;
}

/** Parse removal args (throws before any Office work if nothing is targeted). */
function parseRemoveOptions(args: Record<string, unknown>): RemoveOptions {
  const deleteAll = args.deleteAll === true;
  const shapeIds = Array.isArray(args.shapeIds)
    ? (args.shapeIds as unknown[]).filter((x): x is string => typeof x === "string")
    : [];
  if (!deleteAll && shapeIds.length === 0) {
    throw new Error("Provide shapeIds (from get_master_info) or deleteAll:true — nothing to remove.");
  }
  return { deleteAll, shapeIds, includePlaceholders: args.includePlaceholders === true };
}

/**
 * Delete shapes from a surface (master or layout). Resolves targets by id (or
 * all when deleteAll), SKIPS placeholder shapes unless includePlaceholders is
 * set (deleting master placeholders breaks slide inheritance), and reports what
 * was deleted vs skipped. Shape.delete() is PowerPointApi 1.3.
 */
async function removeShapesFromSurface(
  context: PowerPoint.RequestContext,
  surface: ShapeSurface,
  opts: RemoveOptions
): Promise<{ deleted: RemovedShape[]; skippedPlaceholders: RemovedShape[]; remaining: number }> {
  const shapes = surface.shapes;
  shapes.load("items/id,items/name,items/type");
  await context.sync();

  let targets: PowerPoint.Shape[];
  if (opts.deleteAll) {
    targets = shapes.items;
  } else {
    const byId = new Map(shapes.items.map((s) => [s.id, s]));
    const missing = opts.shapeIds.filter((id) => !byId.has(id));
    if (missing.length > 0) {
      throw new Error(
        `No shape(s) with id ${missing.map((q) => `"${q}"`).join(", ")} on this surface — ` +
          "call get_master_info to list current shape ids."
      );
    }
    targets = opts.shapeIds.map((id) => byId.get(id) as PowerPoint.Shape);
  }

  const isPlaceholder = (type: unknown) =>
    typeof type === "string" && type.toLowerCase() === "placeholder";
  const meta = (s: PowerPoint.Shape): RemovedShape => ({ id: s.id, name: s.name, type: String(s.type) });

  const skipped = targets.filter((s) => isPlaceholder(s.type) && !opts.includePlaceholders);
  const toDelete = targets.filter((s) => !(isPlaceholder(s.type) && !opts.includePlaceholders));
  const deleted = toDelete.map(meta);
  toDelete.forEach((s) => s.delete());
  await context.sync();

  shapes.load("items");
  await context.sync();
  return { deleted, skippedPlaceholders: skipped.map(meta), remaining: shapes.items.length };
}

/** JSON-schema properties for removing shapes (shared by master/layout). */
const REMOVE_PROPS: Record<string, unknown> = {
  shapeIds: {
    type: "array",
    items: { type: "string" },
    description: "Ids to delete (from get_master_info). Omit when using deleteAll.",
  },
  deleteAll: {
    type: "boolean",
    description: "Delete every non-placeholder shape on the surface. (Placeholders still need includePlaceholders.)",
  },
  includePlaceholders: {
    type: "boolean",
    description:
      "Also delete Placeholder shapes (title/body/date/footer/slide-number). Off by default — deleting them breaks how slides inherit those regions.",
  },
};

/** JSON-schema properties for a background fill (shared by master/layout). */
const BACKGROUND_PROPS: Record<string, unknown> = {
  color: { type: "string", description: 'Solid fill colour, hex "#RRGGBB".' },
  transparency: { type: "number", description: "0.0 (opaque) – 1.0 (clear)." },
  useUploadedImage: {
    type: "string",
    description: "Picture-fill source: a chat upload, attached-folder path, or Usable fileId.",
  },
  useGeneratedImage: { type: "boolean", description: "Picture-fill from the most recent generated image." },
  imageBase64: { type: "string", description: "Picture-fill as base64/data-URL." },
  imageUrl: { type: "string", description: "Picture-fill URL (CORS-accessible)." },
};

/** JSON-schema for one entry of the `shapes` array (shared by master/layout). */
const SHAPE_ITEM_SCHEMA = {
  type: "object",
  properties: {
    shapeType: { type: "string", description: `One of: ${GEOMETRIC_SHAPE_TYPES.join(", ")}.` },
    left: { type: "number", description: "Left in points (960×540 canvas)." },
    top: { type: "number", description: "Top in points." },
    width: { type: "number", description: "Width in points." },
    height: { type: "number", description: "Height in points." },
    fillColor: { type: "string", description: 'Solid fill colour, hex "#RRGGBB".' },
    fillTransparency: { type: "number", description: "0.0 (opaque) – 1.0 (clear)." },
    lineColor: { type: "string", description: 'Border colour, hex "#RRGGBB".' },
    lineWeight: { type: "number", description: "Border weight in points." },
    text: { type: "string", description: "Optional text inside the shape." },
    fontName: { type: "string" },
    fontSize: { type: "number" },
    fontColor: { type: "string" },
    bold: { type: "boolean" },
    italic: { type: "boolean" },
    sendToBack: {
      type: "boolean",
      description: "Push behind placeholders — use for full-bleed background blocks and bands.",
    },
  },
  required: ["shapeType", "left", "top", "width", "height"],
};

export const masterTools: PptxTool[] = [
  // ─── Get Master Info ──────────────────────────────────────────────────────

  {
    minRequirementSet: "1.3",
    schema: {
      name: "get_master_info",
      description:
        "Inspect the first slide master: its shapes (these appear on EVERY slide that does not " +
        "override them), its layouts, and — where the host supports it — the theme colors. Use " +
        "before editing the master or theme.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return PowerPoint.run(async (context) => {
        const masters = context.presentation.slideMasters;
        masters.load("items/name,items/id");
        await context.sync();
        if (masters.items.length === 0) throw new Error("No slide masters in this presentation.");

        const master = masters.items[0];
        const shapes = master.shapes;
        shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");
        const layouts = master.layouts;
        layouts.load("items/name");
        await context.sync();

        // Theme colors are PowerPointApi 1.10 — probe gracefully.
        let themeColors: Record<string, string> | undefined;
        try {
          const scheme = master.themeColorScheme;
          const reads: Array<[string, OfficeExtension.ClientResult<string>]> = Object.entries(
            THEME_SLOTS
          ).map(([key, slot]) => [
            key,
            scheme.getThemeColor(slot as unknown as PowerPoint.ThemeColor),
          ]);
          await context.sync();
          themeColors = {};
          for (const [key, result] of reads) themeColors[key] = result.value;
        } catch {
          themeColors = undefined; // host below 1.10 — omit
        }

        return {
          master: master.name,
          masterCount: masters.items.length,
          shapes: shapes.items.map((s, i) => ({
            index: i,
            id: s.id,
            name: s.name,
            type: s.type,
            left: s.left,
            top: s.top,
            width: s.width,
            height: s.height,
          })),
          layouts: layouts.items.map((l, i) => ({ index: i, name: l.name })),
          ...(themeColors ? { themeColors } : { themeColors: "requires PowerPointApi 1.10" }),
        };
      });
    },
  },

  // ─── Apply Master Branding ────────────────────────────────────────────────

  {
    // Master shapes typed since 1.3; the logo path uses fill.setImage (1.8).
    minRequirementSet: "1.8",
    schema: {
      name: "apply_master_branding",
      description:
        "Add branding to the SLIDE MASTER itself — a logo and/or footer text placed once on the " +
        "master appears on every slide (including future ones) without per-slide shapes. This is " +
        "the true master-slide approach; prefer it over apply_deck_branding when the host " +
        "supports it. If the host rejects master edits the error will say so — then fall back to " +
        "apply_deck_branding. Logo source: exactly one of useUploadedImage (chat upload or " +
        "attached-folder path), useGeneratedImage, logoBase64, logoUrl.",
      parameters: {
        type: "object",
        properties: {
          useUploadedImage: {
            type: "string",
            description: "fileName/ref of a chat upload, a path in the attached local folder, OR a Usable workspace fileId (from search-files).",
          },
          useGeneratedImage: { type: "boolean", description: "Use the most recent generated image." },
          logoBase64: { type: "string", description: "Logo as base64/data-URL." },
          logoUrl: { type: "string", description: "Logo URL (CORS-accessible)." },
          logoPosition: {
            type: "string",
            description: '"bottom-right" (default), "bottom-left", or "top-right".',
          },
          footerText: { type: "string", description: "Optional muted footer line on the master." },
        },
      },
    },
    handler: async (args) => {
      const hasLogoSource =
        typeof args.useUploadedImage === "string" ||
        args.useGeneratedImage === true ||
        typeof args.logoBase64 === "string" ||
        typeof args.logoUrl === "string";
      const footerText =
        typeof args.footerText === "string" && args.footerText.length > 0
          ? (args.footerText as string)
          : null;
      if (!hasLogoSource && !footerText) {
        throw new Error("Provide a logo source and/or footerText — nothing to apply.");
      }

      const logo = hasLogoSource
        ? await resolveSlideImage({
            useUploadedImage: args.useUploadedImage,
            useGeneratedImage: args.useGeneratedImage,
            imageBase64: args.logoBase64,
            imageUrl: args.logoUrl,
          })
        : null;

      return PowerPoint.run(async (context) => {
        const masters = context.presentation.slideMasters;
        masters.load("items");
        await context.sync();
        if (masters.items.length === 0) throw new Error("No slide masters in this presentation.");
        const master = masters.items[0];

        const created: Record<string, { id: string }> = {};
        const imageFillShapes: Array<{ shape: PowerPoint.Shape; base64: string }> = [];

        let logoShape: PowerPoint.Shape | null = null;
        if (logo) {
          const rect = computeLogoRect(logo.natural, String(args.logoPosition ?? "bottom-right"));
          logoShape = buildShape(master, { kind: "image", rect, base64: logo.base64 });
          imageFillShapes.push({ shape: logoShape, base64: logo.base64 });
        }
        let footerShape: PowerPoint.Shape | null = null;
        if (footerText) {
          footerShape = buildShape(master, {
            kind: "textbox",
            rect: { left: 24, top: CANVAS.height - 34, width: 400, height: 20 },
            text: footerText,
            font: { size: 10, color: "#8A8F98" },
          });
        }
        if (logoShape) logoShape.load("id");
        if (footerShape) footerShape.load("id");
        await context.sync();

        const imageFills = await ensureImageFills(context, imageFillShapes);
        if (logo && logoShape) {
          recordImageProvenance({
            scope: "master",
            shapeId: logoShape.id,
            source: logo.provenance.source,
            ref: logo.provenance.ref,
            base64: logo.base64,
          });
        }
        if (logoShape) created.brand_logo = { id: logoShape.id };
        if (footerShape) created.brand_footer = { id: footerShape.id };

        return {
          success: true,
          appliedTo: "slide master",
          shapes: created,
          imageFills,
          warnings:
            imageFills.failed > 0
              ? ["The master logo fill did not apply even after a retry — fall back to apply_deck_branding."]
              : [],
          message:
            "Branding applied to the slide master — it should now appear on every slide. Verify " +
            "with get_slide_screenshot on any slide; if it does NOT show, this host doesn't honor " +
            "master shape additions — use apply_deck_branding instead.",
        };
      });
    },
  },

  // ─── Set Theme Colors ─────────────────────────────────────────────────────

  {
    // ThemeColorScheme.setThemeColor is PowerPointApi 1.10.
    minRequirementSet: "1.10",
    schema: {
      name: "set_theme_colors",
      description:
        "Set the presentation THEME colors on the slide master (accent1–6, dark1/2, light1/2, " +
        "hyperlink, followedHyperlink). Theme colors cascade to everything that uses them — the " +
        "real way to apply a brand palette deck-wide. Colors as hex '#RRGGBB'. Only the slots " +
        "you pass are changed.",
      parameters: {
        type: "object",
        properties: {
          colors: {
            type: "object",
            description: 'Slot → hex map, e.g. {"accent1": "#0F6CBD", "dark1": "#1A1A24"}.',
            properties: Object.keys(THEME_SLOTS).reduce(
              (acc, key) => ({ ...acc, [key]: { type: "string" } }),
              {} as Record<string, unknown>
            ),
          },
        },
        required: ["colors"],
      },
    },
    handler: async (args) => {
      const colors = (args.colors ?? {}) as Record<string, unknown>;
      const entries = Object.entries(colors).filter(
        ([key, value]) => THEME_SLOTS[key] && typeof value === "string"
      );
      if (entries.length === 0) {
        throw new Error(
          `No valid theme color slots given. Valid slots: ${Object.keys(THEME_SLOTS).join(", ")}.`
        );
      }
      return PowerPoint.run(async (context) => {
        const masters = context.presentation.slideMasters;
        masters.load("items");
        await context.sync();
        if (masters.items.length === 0) throw new Error("No slide masters in this presentation.");
        const scheme = masters.items[0].themeColorScheme;

        for (const [key, hex] of entries) {
          scheme.setThemeColor(
            THEME_SLOTS[key] as unknown as PowerPoint.ThemeColor,
            String(hex).replace(/^#/, "")
          );
        }
        await context.sync();

        // Read back to confirm the writes stuck.
        const reads: Array<[string, OfficeExtension.ClientResult<string>]> = entries.map(
          ([key]) => [key, scheme.getThemeColor(THEME_SLOTS[key] as unknown as PowerPoint.ThemeColor)]
        );
        await context.sync();
        const applied: Record<string, string> = {};
        for (const [key, result] of reads) applied[key] = result.value;

        return { success: true, applied, message: "Theme colors set on the master — verify with a screenshot." };
      });
    },
  },

  // ─── Set Master Background ────────────────────────────────────────────────

  {
    // SlideMaster.background.fill is PowerPointApi 1.10.
    minRequirementSet: "1.10",
    schema: {
      name: "set_master_background",
      description:
        "Set the SLIDE MASTER's background fill — a solid colour or a picture — so it appears " +
        "behind every slide (e.g. a deck-wide navy or light canvas). This is the true background; " +
        "prefer it over a full-bleed rectangle (no z-order fights with placeholders). For a solid " +
        "colour pass `color`; for a picture pass one image source. Verify with get_slide_screenshot.",
      parameters: { type: "object", properties: BACKGROUND_PROPS },
    },
    handler: async (args) => {
      const spec = await resolveBackgroundSpec(args);
      return PowerPoint.run(async (context) => {
        const master = await firstMaster(context);
        await applyBackgroundFill(context, master.background.fill, spec);
        return {
          success: true,
          appliedTo: "slide master",
          fill: spec.image ? "picture" : "solid",
          message:
            "Master background set — it should appear on every slide. Verify with " +
            "get_slide_screenshot; if it does NOT show, this host doesn't honor master background " +
            "edits — set per-slide backgrounds via compose_deck's palette instead.",
        };
      });
    },
  },

  // ─── Add Master Shapes ────────────────────────────────────────────────────

  {
    // addGeometricShape is 1.4; sendToBack uses setZOrder (1.8).
    minRequirementSet: "1.8",
    schema: {
      name: "add_master_shapes",
      description:
        "Add geometry to the SLIDE MASTER so it appears on EVERY slide — yellow top banners, " +
        "colour bands, accent circles, footer rules. Pass an array of shape specs; set " +
        "sendToBack:true on full-bleed background blocks so they sit behind slide content. For a " +
        "plain deck-wide background colour prefer set_master_background. Verify with " +
        "get_slide_screenshot; if the shapes don't render, the host rejected master edits — fall " +
        "back to apply_deck_branding.",
      parameters: {
        type: "object",
        properties: {
          shapes: {
            type: "array",
            description: "Shapes to add to the master. Geometry in points on the 960×540 canvas.",
            items: SHAPE_ITEM_SCHEMA,
          },
        },
        required: ["shapes"],
      },
    },
    handler: async (args) => {
      const entries = parseShapeEntries(args);
      return PowerPoint.run(async (context) => {
        const master = await firstMaster(context);
        const shapes = await addPlansToSurface(context, master, entries);
        return {
          success: true,
          appliedTo: "slide master",
          shapes,
          message:
            "Shapes added to the slide master — they should appear on every slide. Verify with " +
            "get_slide_screenshot; if they do NOT show, this host doesn't honor master shape " +
            "additions — use apply_deck_branding instead.",
        };
      });
    },
  },

  // ─── Set Layout Background ─────────────────────────────────────────────────

  {
    // SlideLayout.background.fill is PowerPointApi 1.10.
    minRequirementSet: "1.10",
    schema: {
      name: "set_layout_background",
      description:
        "Set the background fill of a single LAYOUT (by name or index) — appears only on slides " +
        "using that layout, not the whole deck. Use for section-divider layouts (e.g. a navy " +
        "background on just the 'Section Header' layout). Target with layoutName (preferred) or " +
        "layoutIndex; call get_master_info to list layouts. Solid `color` or one picture source.",
      parameters: {
        type: "object",
        properties: {
          layoutName: { type: "string", description: "Layout name (case-insensitive). Preferred." },
          layoutIndex: { type: "number", description: "0-based layout index (alternative to layoutName)." },
          ...BACKGROUND_PROPS,
        },
      },
    },
    handler: async (args) => {
      const spec = await resolveBackgroundSpec(args);
      return PowerPoint.run(async (context) => {
        const master = await firstMaster(context, { layouts: true });
        const layout = resolveLayout(master, { layoutName: args.layoutName, layoutIndex: args.layoutIndex });
        await applyBackgroundFill(context, layout.background.fill, spec);
        return {
          success: true,
          appliedTo: `layout "${layout.name}"`,
          fill: spec.image ? "picture" : "solid",
          message: `Background set on layout "${layout.name}" — verify with get_slide_screenshot on a slide that uses it.`,
        };
      });
    },
  },

  // ─── Add Layout Shapes ────────────────────────────────────────────────────

  {
    minRequirementSet: "1.8",
    schema: {
      name: "add_layout_shapes",
      description:
        "Add geometry to a single LAYOUT (by name or index) — appears only on slides using that " +
        "layout. This is the right home for section-divider circles, layout-specific card " +
        "patterns, and per-section accents (unlike add_master_shapes, which hits every slide). " +
        "Target with layoutName (preferred) or layoutIndex; call get_master_info to list layouts. " +
        "Set sendToBack:true on background blocks. Verify with get_slide_screenshot.",
      parameters: {
        type: "object",
        properties: {
          layoutName: { type: "string", description: "Layout name (case-insensitive). Preferred." },
          layoutIndex: { type: "number", description: "0-based layout index (alternative to layoutName)." },
          shapes: {
            type: "array",
            description: "Shapes to add to the layout. Geometry in points on the 960×540 canvas.",
            items: SHAPE_ITEM_SCHEMA,
          },
        },
        required: ["shapes"],
      },
    },
    handler: async (args) => {
      const entries = parseShapeEntries(args);
      return PowerPoint.run(async (context) => {
        const master = await firstMaster(context, { layouts: true });
        const layout = resolveLayout(master, { layoutName: args.layoutName, layoutIndex: args.layoutIndex });
        const shapes = await addPlansToSurface(context, layout, entries);
        return {
          success: true,
          appliedTo: `layout "${layout.name}"`,
          shapes,
          message: `Shapes added to layout "${layout.name}" — verify with get_slide_screenshot on a slide that uses it.`,
        };
      });
    },
  },

  // ─── Remove Master Shapes ─────────────────────────────────────────────────

  {
    // Shape.delete() is PowerPointApi 1.3.
    minRequirementSet: "1.3",
    schema: {
      name: "remove_master_shapes",
      description:
        "Delete shapes from the SLIDE MASTER by id (from get_master_info) or deleteAll:true — use to " +
        "strip default master decoration before applying your own branding. Placeholder shapes " +
        "(title/body/date/footer/slide-number) are SKIPPED unless includePlaceholders:true, because " +
        "deleting them breaks how slides inherit those regions. Returns what was deleted vs skipped; " +
        "verify with get_slide_screenshot. If the host rejects master edits the error will say so.",
      parameters: { type: "object", properties: REMOVE_PROPS },
    },
    handler: async (args) => {
      const opts = parseRemoveOptions(args);
      return PowerPoint.run(async (context) => {
        const master = await firstMaster(context);
        const result = await removeShapesFromSurface(context, master, opts);
        return {
          success: true,
          appliedTo: "slide master",
          ...result,
          message:
            "Master shapes removed — verify with get_slide_screenshot on any slide. " +
            (result.skippedPlaceholders.length > 0
              ? `${result.skippedPlaceholders.length} placeholder(s) were kept; pass includePlaceholders:true to delete those too.`
              : ""),
        };
      });
    },
  },

  // ─── Remove Layout Shapes ─────────────────────────────────────────────────

  {
    minRequirementSet: "1.3",
    schema: {
      name: "remove_layout_shapes",
      description:
        "Delete shapes from a single LAYOUT (by name or index) by id or deleteAll:true — affects only " +
        "slides using that layout. Target with layoutName (preferred) or layoutIndex; call " +
        "get_master_info to list layouts. Placeholder shapes are SKIPPED unless " +
        "includePlaceholders:true (deleting them breaks slide inheritance). Verify with get_slide_screenshot.",
      parameters: {
        type: "object",
        properties: {
          layoutName: { type: "string", description: "Layout name (case-insensitive). Preferred." },
          layoutIndex: { type: "number", description: "0-based layout index (alternative to layoutName)." },
          ...REMOVE_PROPS,
        },
      },
    },
    handler: async (args) => {
      const opts = parseRemoveOptions(args);
      return PowerPoint.run(async (context) => {
        const master = await firstMaster(context, { layouts: true });
        const layout = resolveLayout(master, { layoutName: args.layoutName, layoutIndex: args.layoutIndex });
        const result = await removeShapesFromSurface(context, layout, opts);
        return {
          success: true,
          appliedTo: `layout "${layout.name}"`,
          ...result,
          message:
            `Shapes removed from layout "${layout.name}" — verify with get_slide_screenshot. ` +
            (result.skippedPlaceholders.length > 0
              ? `${result.skippedPlaceholders.length} placeholder(s) were kept; pass includePlaceholders:true to delete those too.`
              : ""),
        };
      });
    },
  },
];
